/**
 * CoordinateTransformer
 *
 * Handles coordinate transformations between different spaces:
 * - Screen space (viewport pixel coordinates)
 * - World space (3D scene coordinates)
 * - Mask space (normalized [0,1] coordinates relative to captured image)
 * - Pixel space (image pixel coordinates)
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { CapturedImage } from '../../../../utils/segmentation/ISegmentationStrategy';

/**
 * Eye-space encoding metadata captured alongside a position map. The
 * shader writes each point's eye-X, eye-Y, eye-Z normalized to these
 * bounds (12+12+8 bits across RGBA). Multiplying decoded eye-XYZ by
 * `inverseViewMatrix` recovers the world position of that point —
 * works for any camera orientation (top-down, wall-perpendicular, etc.).
 */
export interface PositionMapEncoding {
  eyeMin: [number, number, number];
  eyeMax: [number, number, number];
  inverseViewMatrix: THREE.Matrix4;
}

/**
 * The "base plane" the LineTool's prompts/shape vertices live on. For
 * rooms it's horizontal at floor Z; for door/window strips it's
 * vertical on the focused wall. The frame defines an orthonormal local
 * 2D basis (u, v) on the plane plus the world-space normal — modules
 * that used to do `wp.z = floorHeight` (XY math hardcoded to the floor
 * plane) now transform world coords into this frame, run the same 2D
 * math in (u, v), then transform back. In the room case the frame is
 * `u=(1,0,0)`, `v=(0,1,0)`, `n=(0,0,1)` with origin at floor height,
 * so world-XY equals local-uv and behavior is identical.
 *
 * The kind adapter (RoomKindAdapter / DoorWindowEditKindAdapter)
 * installs the appropriate frame when the LineTool enters its modes;
 * `clear()` drops it back to null. While null, helpers fall back to
 * the identity transform — the same as the pre-frame behavior.
 */
export interface BasePlaneFrame {
  origin: THREE.Vector3;
  /** Unit in-plane axis #1, world-space. */
  u: THREE.Vector3;
  /** Unit in-plane axis #2, world-space. Should equal n × u up to sign. */
  v: THREE.Vector3;
  /** Unit normal, world-space. */
  normal: THREE.Vector3;
}

export class CoordinateTransformer {
  private viewer: Viewer;
  private maskCorners: THREE.Vector3[] = [];
  private capturedImage: CapturedImage | null = null;
  private positionMapEncoding: PositionMapEncoding | null = null;

  /**
   * Provider for the active base-plane frame. The transformer holds NO
   * cached frame — every `getBasePlaneFrame()` call invokes the provider
   * so the frame is always derived from whichever plane is currently
   * installed on `viewer.viewerMode.associatedPlane` (and the active
   * mode's floor selection / editing shape). This collapses what used to
   * be two sources of truth (`associatedPlane` and a cached
   * `basePlaneFrame`) into one: `associatedPlane` is canonical, the
   * frame is a pure function of it. Pre-refactor the cache went stale
   * whenever something swapped `associatedPlane` without retriggering
   * the cache refresh (e.g. wall A → X-Ray → wall B in DoorWindowTool),
   * causing prompt drags + shape preview to project against the wrong
   * plane.
   */
  private basePlaneFrameProvider: (() => BasePlaneFrame | null) | null = null;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * Register the provider that computes the active base-plane frame on
   * demand. Pass null to clear. Modes register exactly once at activate
   * and never re-call this — the provider closes over `this` so reads
   * pick up live state (associatedPlane, currentBasePlane, editingShape).
   *
   * Handedness is validated on the first call after install (one-shot
   * via the warned flag) so adapter mistakes surface immediately without
   * spamming the console on every per-frame read.
   */
  setBasePlaneFrameProvider(provider: (() => BasePlaneFrame | null) | null): void {
    this.basePlaneFrameProvider = provider;
    this.basePlaneFrameWarned = false;
  }

  private basePlaneFrameWarned = false;

  /**
   * Compute the active base-plane frame via the registered provider.
   * Returns null when no provider is installed or the provider itself
   * returns null — consumers fall back to "world XY is the plane"
   * identity behavior (matches the pre-frame snapper module semantics).
   *
   * Validates handedness once-per-provider: the LineTool snap math
   * computes in-plane perpendiculars as `(-localDir.y, localDir.x, 0)`
   * which matches the world-XY perpendicular only when `v = normal × u`
   * (right-handed). A wrong-handed frame would silently invert every
   * perpendicular and break snapping in subtle ways.
   */
  getBasePlaneFrame(): BasePlaneFrame | null {
    const frame = this.basePlaneFrameProvider ? this.basePlaneFrameProvider() : null;
    if (frame && !this.basePlaneFrameWarned) {
      const expectedV = new THREE.Vector3().crossVectors(frame.normal, frame.u);
      if (expectedV.distanceTo(frame.v) > 1e-3) {
        console.warn('[CoordinateTransformer] BasePlaneFrame is not right-handed: v should equal normal × u. Snap math will produce wrong-signed perpendiculars.', { u: frame.u.toArray(), v: frame.v.toArray(), normal: frame.normal.toArray(), expectedV: expectedV.toArray() });
      }
      this.basePlaneFrameWarned = true;
    }
    return frame;
  }

  /**
   * World → plane-local. Returns `(u·d, v·d, n·d)` where `d = p - origin`.
   * Identity (clone of p) when no frame is set, so module code that
   * always-on-calls this works whether a frame is installed or not.
   */
  toBasePlaneLocal(p: THREE.Vector3): THREE.Vector3 {
    const f = this.getBasePlaneFrame();
    if (!f) return p.clone();
    const d = p.clone().sub(f.origin);
    return new THREE.Vector3(d.dot(f.u), d.dot(f.v), d.dot(f.normal));
  }

  /**
   * Plane-local → world. Inverse of `toBasePlaneLocal`. Identity
   * (clone of p) when no frame is set.
   */
  fromBasePlaneLocal(p: THREE.Vector3): THREE.Vector3 {
    const f = this.getBasePlaneFrame();
    if (!f) return p.clone();
    return f.origin.clone().addScaledVector(f.u, p.x).addScaledVector(f.v, p.y).addScaledVector(f.normal, p.z);
  }

  /**
   * Direction-only world → local (no translation by origin).
   */
  toBasePlaneLocalDir(d: THREE.Vector3): THREE.Vector3 {
    const f = this.getBasePlaneFrame();
    if (!f) return d.clone();
    return new THREE.Vector3(d.dot(f.u), d.dot(f.v), d.dot(f.normal));
  }

  /**
   * Direction-only local → world.
   */
  fromBasePlaneLocalDir(d: THREE.Vector3): THREE.Vector3 {
    const f = this.getBasePlaneFrame();
    if (!f) return d.clone();
    return new THREE.Vector3().addScaledVector(f.u, d.x).addScaledVector(f.v, d.y).addScaledVector(f.normal, d.z);
  }

  /**
   * Set the mask corners (4 world-space corners of the captured area)
   */
  setMaskCorners(corners: THREE.Vector3[]): void {
    if (corners.length !== 4) {
      throw new Error('Mask corners must have exactly 4 points');
    }
    this.maskCorners = corners;
  }

  /**
   * Set the captured image for pixel space conversions
   */
  setCapturedImage(image: CapturedImage | null): void {
    this.capturedImage = image;
  }

  /** Eye-space encoding metadata for the cached position map. */
  setPositionMapEncoding(encoding: PositionMapEncoding | null): void {
    this.positionMapEncoding = encoding;
  }

  getPositionMapEncoding(): PositionMapEncoding | null {
    return this.positionMapEncoding;
  }

  /**
   * Decode eye-space position from a position-map RGBA quadruple and
   * convert it to a world position via the cached inverse view matrix.
   * Returns null when the pixel is empty (all zero) or no encoding has
   * been captured yet.
   */
  decodePositionMapPixel(r: number, g: number, b: number, a: number): THREE.Vector3 | null {
    const enc = this.positionMapEncoding;
    if (!enc) return null;
    if (r === 0 && g === 0 && a === 0) return null;

    const xHigh = r;
    const yHigh = g;
    const xLow = (a >> 4) & 0x0f;
    const yLow = a & 0x0f;
    const x12 = (xHigh << 4) | xLow;
    const y12 = (yHigh << 4) | yLow;
    const normX = x12 / 4095;
    const normY = y12 / 4095;
    const normZ = b / 255;

    const eyeX = enc.eyeMin[0] + normX * (enc.eyeMax[0] - enc.eyeMin[0]);
    const eyeY = enc.eyeMin[1] + normY * (enc.eyeMax[1] - enc.eyeMin[1]);
    // Shader stored positive depth (-eye.z); reapply the OpenGL sign to
    // get the eye-space point that the inverse view matrix expects.
    const eyeZ = -(enc.eyeMin[2] + normZ * (enc.eyeMax[2] - enc.eyeMin[2]));

    return new THREE.Vector3(eyeX, eyeY, eyeZ).applyMatrix4(enc.inverseViewMatrix);
  }


  /**
   * Get mask corners
   */
  getMaskCorners(): THREE.Vector3[] {
    return this.maskCorners;
  }

  /**
   * Clear all stored data
   */
  clear(): void {
    this.maskCorners = [];
    this.capturedImage = null;
    this.positionMapEncoding = null;
    this.basePlaneFrameProvider = null;
    this.basePlaneFrameWarned = false;
  }

  /**
   * Convert screen coordinates to world space using raycasting or associated plane.
   * Uses local coordinate system when far from origin to avoid floating point precision issues.
   */
  screenToWorld(screen: THREE.Vector2): THREE.Vector3 | null {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const ndcX = (screen.x / viewportWidth) * 2 - 1;
    const ndcY = -(screen.y / viewportHeight) * 2 + 1;

    // If there's an associated plane, unproject directly onto it
    const associatedPlane = this.viewer.viewerMode.associatedPlane;
    if (associatedPlane) {
      // Use local coordinate system relative to plane position for better precision
      // This avoids precision loss when coordinates are far from origin
      const planeOrigin = associatedPlane.position.clone();

      // Get camera position relative to plane origin
      const localCameraPos = camera.position.clone().sub(planeOrigin);

      // Create a temporary camera matrix in local space
      const localCameraMatrix = camera.matrixWorld.clone();
      localCameraMatrix.setPosition(localCameraPos);

      // Create temporary camera for local-space unprojection
      const tempCamera = camera.clone();
      tempCamera.matrixWorld.copy(localCameraMatrix);
      tempCamera.matrixWorldInverse.copy(localCameraMatrix).invert();
      tempCamera.projectionMatrixInverse.copy(camera.projectionMatrixInverse);

      // Unproject in local space (relative to plane origin)
      const nearPoint = new THREE.Vector3(ndcX, ndcY, -1).unproject(tempCamera);
      const farPoint = new THREE.Vector3(ndcX, ndcY, 1).unproject(tempCamera);

      // Create ray in local space
      const rayDirection = new THREE.Vector3().subVectors(farPoint, nearPoint).normalize();
      const ray = new THREE.Ray(nearPoint, rayDirection);

      // Plane at local origin (since we shifted everything)
      const localPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(associatedPlane.getNormal(), new THREE.Vector3(0, 0, 0));

      const localIntersection = ray.intersectPlane(localPlane, new THREE.Vector3());
      if (localIntersection) {
        // Transform back to world space
        return localIntersection.add(planeOrigin);
      }
    }

    // Otherwise use regular raycasting against scene objects
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const intersections = raycaster.intersectObjects(this.viewer.scene.scene.children, true);
    if (intersections.length > 0) {
      return intersections[0].point;
    }

    // Fallback: unproject at z=0
    return new THREE.Vector3(ndcX, ndcY, 0).unproject(camera);
  }

  /**
   * Convert world position to screen coordinates
   */
  worldToScreen(worldPos: THREE.Vector3): THREE.Vector2 {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    const projected = worldPos.clone().project(camera);
    const screenX = ((projected.x + 1) * viewportWidth) / 2;
    const screenY = ((-projected.y + 1) * viewportHeight) / 2;

    return new THREE.Vector2(screenX, screenY);
  }

  /**
   * Convert world position to mask space [0, 1] x [0, 1] coordinates
   */
  worldToMaskSpace(worldPos: THREE.Vector3): THREE.Vector2 {
    if (this.maskCorners.length !== 4) {
      throw new Error('Mask corners not set');
    }

    // Use the mask corners to establish a coordinate system
    const topLeft = this.maskCorners[0];
    const topRight = this.maskCorners[1];
    const bottomLeft = this.maskCorners[3];

    // Create vectors along the edges
    const rightVec = new THREE.Vector3().subVectors(topRight, topLeft);
    const downVec = new THREE.Vector3().subVectors(bottomLeft, topLeft);

    // Vector from top-left to the world position
    const posVec = new THREE.Vector3().subVectors(worldPos, topLeft);

    // Project onto the right and down vectors to get normalized coordinates
    const u = posVec.dot(rightVec) / rightVec.lengthSq();
    const v = posVec.dot(downVec) / downVec.lengthSq();

    return new THREE.Vector2(u, v);
  }

  /**
   * Convert mask space [0, 1] x [0, 1] coordinates to world position
   */
  maskSpaceToWorld(maskPos: THREE.Vector2): THREE.Vector3 {
    if (this.maskCorners.length !== 4) {
      throw new Error('Mask corners not set');
    }

    // Convert normalized mask coordinates back to world space
    const topLeft = this.maskCorners[0];
    const topRight = this.maskCorners[1];
    const bottomLeft = this.maskCorners[3];

    // Interpolate along the edges
    const rightVec = new THREE.Vector3().subVectors(topRight, topLeft);
    const downVec = new THREE.Vector3().subVectors(bottomLeft, topLeft);

    const worldPos = topLeft.clone().add(rightVec.multiplyScalar(maskPos.x)).add(downVec.multiplyScalar(maskPos.y));

    return worldPos;
  }

  /**
   * Convert pixel space coordinates to world space using bilinear interpolation
   * @param pixelX X coordinate in pixel space (0 to image width)
   * @param pixelY Y coordinate in pixel space (0 to image height)
   */
  pixelToWorld(pixelX: number, pixelY: number): THREE.Vector3 | null {
    if (!this.capturedImage || this.maskCorners.length !== 4) {
      return null;
    }

    // Convert from pixel space to normalized [0,1]
    const u = pixelX / this.capturedImage.width;
    const v = pixelY / this.capturedImage.height;

    // Bilinear interpolation using mask corners
    const topLeft = this.maskCorners[0];
    const topRight = this.maskCorners[1];
    const bottomRight = this.maskCorners[2];
    const bottomLeft = this.maskCorners[3];

    const top = new THREE.Vector3().lerpVectors(topLeft, topRight, u);
    const bottom = new THREE.Vector3().lerpVectors(bottomLeft, bottomRight, u);
    const worldPos = new THREE.Vector3().lerpVectors(top, bottom, v);

    return worldPos;
  }

  /**
   * Calculate pixels per meter from mask corners and captured image
   */
  calculatePixelsPerMeter(): number | undefined {
    if (this.maskCorners.length !== 4 || !this.capturedImage) {
      return undefined;
    }

    const topLeft = this.maskCorners[0];
    const topRight = this.maskCorners[1];
    const bottomLeft = this.maskCorners[3];

    const worldWidth = topLeft.distanceTo(topRight);
    const worldHeight = topLeft.distanceTo(bottomLeft);

    // Average the pixels-per-meter ratio from both dimensions
    const pixelsPerMeterX = this.capturedImage.width / worldWidth;
    const pixelsPerMeterY = this.capturedImage.height / worldHeight;

    return (pixelsPerMeterX + pixelsPerMeterY) / 2;
  }

  /**
   * Calculate area of a screen-space box in square meters
   */
  calculateBoxAreaM2(start: THREE.Vector2, end: THREE.Vector2): number {
    // Get the 4 corners of the box in screen space
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    const corners = [new THREE.Vector2(minX, minY), new THREE.Vector2(maxX, minY), new THREE.Vector2(maxX, maxY), new THREE.Vector2(minX, maxY)];

    // Unproject corners to world space
    const worldCorners = corners.map((corner) => this.screenToWorld(corner)).filter((c) => c !== null) as THREE.Vector3[];

    if (worldCorners.length !== 4) return 0;

    // Calculate area using shoelace formula (two triangles)
    const triangle1Area = this.calculateTriangleArea(worldCorners[0], worldCorners[1], worldCorners[2]);
    const triangle2Area = this.calculateTriangleArea(worldCorners[0], worldCorners[2], worldCorners[3]);

    return triangle1Area + triangle2Area;
  }

  /**
   * Convert pixels to centimeters using pixelsPerMeter ratio
   */
  pixelsToCm(pixels: number): number {
    const pixelsPerMeter = this.calculatePixelsPerMeter();
    if (!pixelsPerMeter) return pixels; // Fallback if no scale available
    return (pixels / pixelsPerMeter) * 100;
  }

  /**
   * Convert centimeters to pixels using pixelsPerMeter ratio
   */
  cmToPixels(cm: number): number {
    const pixelsPerMeter = this.calculatePixelsPerMeter();
    if (!pixelsPerMeter) return cm; // Fallback if no scale available
    return (cm / 100) * pixelsPerMeter;
  }

  /**
   * Convert a world-space distance to screen-space pixels.
   * This is useful for drawing circles or other shapes that need to maintain
   * a consistent world-space size regardless of camera zoom.
   */
  worldDistanceToScreenDistance(worldDistance: number): number {
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;

    if (camera.type === 'OrthographicCamera') {
      const orthoCamera = camera as THREE.OrthographicCamera;
      // For orthographic camera, calculate based on frustum width
      const frustumWidth = orthoCamera.right - orthoCamera.left;
      return (worldDistance / frustumWidth) * viewportWidth;
    } else {
      // For perspective camera, use a reference point (center of mask or scene pivot)
      // This is an approximation - the actual screen size varies with distance
      const referencePoint = this.maskCorners.length === 4 ? this.maskCorners[0].clone().add(this.maskCorners[2]).multiplyScalar(0.5) : (this.viewer.scene.view?.getPivot()?.clone() ?? new THREE.Vector3());

      const distance = camera.position.distanceTo(referencePoint);

      // Calculate approximate screen pixels using perspective projection
      // FOV is in radians for THREE.js
      const perspCamera = camera as THREE.PerspectiveCamera;
      const vFov = THREE.MathUtils.degToRad(perspCamera.fov);
      const frustumHeight = 2 * distance * Math.tan(vFov / 2);
      const viewportHeight = this.viewer.renderArea.clientHeight;

      return (worldDistance / frustumHeight) * viewportHeight;
    }
  }

  /**
   * Calculate area of a triangle in 3D space
   */
  private calculateTriangleArea(p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3): number {
    const v1 = new THREE.Vector3().subVectors(p2, p1);
    const v2 = new THREE.Vector3().subVectors(p3, p1);
    const cross = new THREE.Vector3().crossVectors(v1, v2);
    return cross.length() / 2;
  }
}
